import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import {
  ChevronRight,
  Award,
  Clock,
  GraduationCap,
  ArrowRight,
  MessageCircle,
  Phone,
  Plus,
  Minus,
  PersonStanding,
  Monitor,
  Dumbbell,
  Sparkles,
} from "lucide-react";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";

const PAGE_PATH = "/gynecomastia-surgery-mumbai";
const PAGE_URL = `https://drvickyjain.com${PAGE_PATH}`;
const MALE_NIPPLE_PATH = "/male-nipple-reduction-mumbai";
const LIPO_PATH = "/liposuction-surgery-mumbai";
const BUCCAL_PATH = "/buccal-fat-removal-mumbai";

const GYNECO_FAQS = [
  {
    question: "Can exercise get rid of gynecomastia?",
    answer:
      "Exercise reduces chest fat but does not remove glandular breast tissue. True gynecomastia involves glandular tissue beneath the nipple that is not fat and does not respond to caloric deficit or exercise. Many men with gynecomastia who are fit and lean still have the firm glandular tissue behind the nipple because exercise cannot target this specific tissue. Surgery is the only treatment that removes it.",
  },
  {
    question: "Is gynecomastia surgery permanent?",
    answer:
      "Yes, when the glandular tissue is completely removed. Excised glandular tissue does not regenerate. However, if anabolic steroid use continues after surgery, if a hormonal cause is not addressed, or if significant weight gain occurs, the conditions that caused the gynecomastia can stimulate residual tissue or cause new fat deposition in the chest. Maintaining a stable weight and avoiding anabolic steroids after surgery preserves the result.",
  },
  {
    question: "Will there be visible scars after gynecomastia surgery?",
    answer:
      "The liposuction access points are 3 to 4 millimeters and leave virtually invisible marks. The periareolar incision for gland excision sits at the lower border of the areola within the natural pigmented boundary. This scar fades significantly over 6 to 12 months and is typically not visible at normal viewing distance. Most patients find the scar completely acceptable relative to the improvement in chest appearance.",
  },
  {
    question: "How long does recovery take after gynecomastia surgery?",
    answer:
      "Light desk work resumes within 1 week. Gym and upper body exercise resumes at 3 to 4 weeks. The compression garment is worn for 4 to 6 weeks. Final chest contour is visible at 3 months when all swelling has resolved.",
  },
  {
    question: "Is liposuction alone sufficient for gynecomastia?",
    answer:
      "Only for pure pseudogynecomastia where chest fullness is caused by fat without significant glandular tissue. For true or mixed gynecomastia where firm glandular tissue is present beneath the nipple, liposuction alone leaves the glandular tissue in place. Patients who have had liposuction alone for gynecomastia and still have residual firmness and nipple puffiness had incomplete treatment. Direct gland excision is required to address the glandular component.",
  },
  {
    question: "Can gynecomastia affect only one side?",
    answer:
      "Yes. Asymmetric gynecomastia, where one side is more affected than the other, is common. Surgery treats each side independently based on the composition and volume on each side. The goal is symmetric correction rather than identical treatment of both sides.",
  },
  {
    question: "At what age can gynecomastia surgery be done?",
    answer:
      "Surgery is appropriate at any age after the condition has stabilized. For teenagers, this means after pubertal gynecomastia has persisted beyond 2 years without signs of resolution. For adults, there is no upper age limit for patients in good general health. The decision is based on the stability of the condition and the patient's overall health fitness for surgery.",
  },
  {
    question: "What are the risks of gynecomastia surgery?",
    answer:
      "Swelling and bruising during the first week are expected. Seroma, a collection of fluid under the skin, occurs in a small percentage of cases and is managed by drainage. Asymmetry, residual tissue, and contour irregularities can occur and may require revision in some cases. Loss of nipple sensation is a rare complication that is usually temporary. Choosing an experienced plastic surgeon with specific gynecomastia surgery experience significantly reduces the risk of these outcomes.",
  },
];

const chestLocation = { "@type": "AnatomicalStructure", name: "Chest" };
const plasticSpecialty = "https://schema.org/PlasticSurgery";

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalProcedure",
      name: "Gynecomastia surgery (male breast reduction)",
      description:
        "Surgical reduction of enlarged male breast tissue combining liposuction and direct glandular excision via periareolar approach for a flat, masculine chest contour.",
      procedureType: "Surgical",
      bodyLocation: chestLocation,
      medicalSpecialty: plasticSpecialty,
      url: PAGE_URL,
    },
    {
      "@type": "MedicalCondition",
      name: "Gynecomastia",
      description:
        "Benign enlargement of male breast tissue due to estrogen-androgen imbalance, with glandular, fatty, or mixed composition; treated with individualized surgery when persistent.",
      possibleTreatment: { "@type": "MedicalProcedure", name: "Gynecomastia surgery", url: PAGE_URL },
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: GYNECO_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function GynecomastiaJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

const Breadcrumb = () => {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/cosmetic-surgery-in-mumbai" },
    { name: "Gynecomastia", href: PAGE_PATH },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
};

const HeroSection = () => (
  <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Breast</p>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">Gynecomastia Surgery</h1>
          <div className="w-12 h-[2px] bg-gold mb-5" />
          <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
            Male Breast Reduction Surgery by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
            <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
            <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
          </div>
        </div>
        <div className="flex justify-center lg:justify-end">
          <ProcedurePlaceholder procedureName="Gynecomastia" icon="🔬" size="hero"  imageSrc={HERO.gynecomastia} imageAlt="Gynecomastia treatment"/>
        </div>
      </div>
    </div>
  </section>
);

const WhatIsSection = () => (
  <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">What is Gynecomastia?</h2>
        </div>
        <div className="space-y-6 text-deep-navy/70 leading-relaxed">
          <p>
            Gynecomastia is the enlargement of male breast tissue caused by an imbalance between estrogen and testosterone. It is one of the most common conditions in men, affecting teenagers during puberty, adults, and older men. The enlargement can involve glandular breast tissue, fat, or both. The specific composition determines the appropriate surgical technique.
          </p>
          <p>
            Gynecomastia is not the same as chest fat from being overweight. True gynecomastia involves actual glandular breast tissue beneath the nipple that feels firm or rubbery on examination. This glandular tissue does not respond to diet or exercise. It is structurally identical to female breast glandular tissue and can only be removed surgically. Pseudogynecomastia, by contrast, is caused by fat accumulation without significant glandular tissue and is more common in overweight men. Mixed gynecomastia, the most common presentation, involves both glandular tissue and fat.
          </p>
          <p>
            Dr. Vicky Jain performs gynecomastia surgery for men across all grades of the condition. The procedure combines liposuction for the fat component with direct gland excision for the glandular component, producing a flat, masculine chest contour with minimal scarring and permanent results.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const DrJainApproachSection = () => (
  <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Gynecomastia Surgery</h2>
          <div className="space-y-6 text-white/75 leading-relaxed">
            <p>
              The most important technical decision in gynecomastia surgery is ensuring complete gland removal. Incomplete gland excision leaves a residual palpable firmness behind the nipple that is the most common cause of patient dissatisfaction after the procedure. The gland must be removed to the point where the nipple and areola sit flat against the chest wall with no residual firm disc of tissue beneath them. Dr. Jain uses a periareolar incision, a small cut at the lower border of the areola, to access and remove the gland directly under vision. This direct approach under vision is more reliable for complete gland removal than blind liposuction through the gland.
            </p>
            <p>
              Liposuction addresses the fat component of the chest and is performed alongside gland excision in the majority of patients with mixed gynecomastia. The liposuction is performed first to reduce the surrounding chest fat before the gland is excised, which improves the final contour by removing the fat that was framing the glandular prominence. Post-operative compression garment wear for 4 to 6 weeks is a required component of recovery, not an optional recommendation. The compression reduces swelling, helps the overlying skin contract to the new chest contour, and prevents seroma formation in the space created by the removed tissue.
            </p>
          </div>
        </div>
        <div className="relative">
          <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
          <div
            className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
            style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
          />
          <div className="mt-4 text-center lg:text-left">
            <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const TYPES_GRADES = [
  {
    title: "True Gynecomastia",
    copy: "Caused by genuine glandular breast tissue beneath the nipple. The tissue feels firm or rubbery on examination and cannot be distinguished from normal female breast glandular tissue on histology. Does not reduce with weight loss or exercise. Requires direct surgical excision for complete removal.",
  },
  {
    title: "Pseudogynecomastia",
    copy: "Chest fullness caused by fat accumulation without significant glandular tissue. More common in overweight men. Feels soft on examination and the nipple-areola complex is not particularly prominent relative to the surrounding chest. Responds to liposuction without the need for gland excision.",
  },
  {
    title: "Mixed Gynecomastia",
    copy: "The most common presentation. Both glandular tissue and excess fat contribute to the chest fullness. The gland is firm beneath the nipple and fat is present in the surrounding chest. Requires both liposuction and direct gland excision for a complete result.",
  },
  {
    title: "Grade 1: Mild",
    copy: "Mild enlargement without skin excess. The chest is slightly prominent, particularly around the nipple-areola complex. The nipple may appear slightly puffy. Liposuction and gland excision produce a flat, natural result with minimal surgical intervention.",
  },
  {
    title: "Grade 2 and 3: Moderate",
    copy: "Moderate to significant enlargement. The chest has a visible feminine contour with increasingly prominent nipple-areola complex. Grade 2 has minimal skin excess. Grade 3 has visible skin laxity. Treatment combines liposuction and gland excision, with skin management in Grade 3 cases.",
  },
  {
    title: "Grade 4: Severe",
    copy: "Significant enlargement with female breast-like appearance including skin excess and ptosis of the breast mound. Requires the most comprehensive surgical approach including liposuction, complete gland excision, and skin tightening or nipple repositioning to restore a masculine chest contour.",
  },
];

const TypesGradesSection = () => (
  <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="types-grades-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
      <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Types and Grades of Gynecomastia</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {TYPES_GRADES.map((card) => (
          <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
            <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
            <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const CAUSE_CARDS = [
  {
    title: "Hormonal",
    copy: "Natural decline in testosterone with aging, which increases the relative estrogen effect. Increased body fat, which converts testosterone to estrogen through peripheral aromatization.",
  },
  {
    title: "Lifestyle",
    copy: "Anabolic steroid use is a common cause in younger men. Steroids suppress the hypothalamic-pituitary-testicular axis and produce estrogen-dominant states. Alcohol and cannabis use have also been associated with gynecomastia.",
  },
  {
    title: "Medications",
    copy: "Several medications cause gynecomastia including some antihypertensives, proton pump inhibitors, antipsychotics, antiandrogens, and some antibiotics. A medication review is part of every gynecomastia consultation.",
  },
  {
    title: "Medical conditions",
    copy: "Liver disease, hyperthyroidism, hypogonadism, and tumors producing estrogen or hCG are less common but clinically important causes that require exclusion before surgery.",
  },
];

const CausesSection = () => (
  <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="causes-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="max-w-4xl mx-auto mb-10">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">What Causes Gynecomastia?</h2>
        <p className="text-deep-navy/70 leading-relaxed text-center md:text-left">
          Gynecomastia develops from any imbalance between estrogen and testosterone that shifts the ratio toward estrogen&apos;s stimulatory effect on breast glandular tissue. During puberty, this imbalance is transient and gynecomastia that develops at this stage resolves in most cases within 2 years without treatment. Gynecomastia persisting beyond puberty or developing in adult men has a cause that needs assessment.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        {CAUSE_CARDS.map((c) => (
          <div key={c.title} className="bg-white p-8 border border-[#E8E2D9]">
            <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{c.title}</h3>
            <p className="text-deep-navy/70 leading-relaxed">{c.copy}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ExerciseMisconceptionSection = () => (
  <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="exercise-misconception-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">Why Exercise and Diet Do Not Fix Gynecomastia</h2>
        </div>
        <div className="space-y-6 text-deep-navy/70 leading-relaxed">
          <p>
            This is the most consistent misconception among patients presenting with gynecomastia. The glandular tissue component of true gynecomastia is not fat. It is breast glandular tissue that has exactly the same histological composition as female breast tissue. Fat cells respond to caloric deficit by reducing in size. Glandular tissue does not. No amount of chest exercise, caloric restriction, or weight loss will reduce the volume of glandular breast tissue beneath the nipple.
          </p>
          <p>
            What exercise and weight loss do achieve in gynecomastia is reduction of the surrounding chest fat, which can make the glandular tissue less prominent visually. Some patients in the very early stages of gynecomastia who present primarily with pseudogynecomastia see meaningful improvement with weight loss. But for patients with established glandular tissue, the firm tissue behind the nipple persists regardless of how lean the rest of the body becomes. Surgery is the only treatment that removes this tissue permanently.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const SurgicalTechniqueSection = () => (
  <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="surgical-technique-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">How Gynecomastia Surgery is Performed</h2>
        </div>
        <div className="space-y-6 text-deep-navy/70 leading-relaxed">
          <p>
            Surgery takes 1 to 2 hours under local or general anesthesia depending on the grade of gynecomastia and patient preference. Most patients go home the same day. Dr. Jain first performs liposuction of the chest through small access points to remove the fat component and reduce the overall chest volume. The liposuction is performed with a thin cannula passed through 3 to 4 millimeter access points that leave virtually invisible scars.
          </p>
          <p>
            After liposuction, a small semicircular incision is made at the lower border of the areola, within the natural pigmented boundary of the areola-skin junction where it is least visible. Through this incision, the glandular tissue is directly visualized and excised in its entirety. The firmness of the tissue, visible to the surgeon under direct vision, guides the extent of excision. Tissue is removed until the nipple and areola sit flat against the chest wall with no residual disc of firm tissue palpable beneath them.
          </p>
          <p>
            The incisions are closed with fine sutures. A compression garment is applied in the operating room before the patient wakes. The garment is worn continuously for 4 to 6 weeks. The removed glandular tissue is sent for histological analysis at every case. Results confirming benign breast tissue are reviewed at the post-operative visit.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const RecoveryTimelineSection = () => {
  const milestones = [
    {
      period: "Days 1 to 3",
      description:
        "Mild to moderate chest discomfort, swelling, and bruising. The compression garment stays on continuously. Most patients are mobile the day after surgery. Pain is managed with standard medication.",
      icon: Sparkles,
    },
    {
      period: "Week 1",
      description:
        "Light desk work and normal daily activity resume. The chest appears smaller and flatter even through swelling. The compression garment continues.",
      icon: PersonStanding,
    },
    {
      period: "Weeks 3 to 4",
      description:
        "The compression garment is worn during the day. Swelling reduces significantly. The chest contour improves progressively. Gym and strenuous exercise resume at this point.",
      icon: Dumbbell,
    },
    {
      period: "Month 3",
      description:
        "Final chest contour is visible. All swelling has resolved. The result is assessed at this visit. The periareolar scar is fading and in most cases is difficult to see.",
      icon: Monitor,
    },
  ];
  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const GoodCandidateSection = () => (
  <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="good-candidate-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">Who Is a Good Candidate for Gynecomastia Surgery?</h2>
        </div>
        <div className="space-y-6 text-deep-navy/70 leading-relaxed">
          <p>
            Good candidates are men with gynecomastia that has persisted for more than 1 year, has not responded to conservative measures, and is causing cosmetic concern or psychological distress. The condition should be stable, meaning it has not been changing rapidly in size. Patients should be at or near a stable weight, as significant weight gain after surgery will add fat to the chest even after gland removal.
          </p>
          <p>
            Teenagers with gynecomastia are appropriate candidates for surgery if the condition has persisted beyond puberty, typically more than 2 years after onset, and is not resolving. There is no advantage to waiting longer beyond this point in persistent pubertal gynecomastia. Patients who are using anabolic steroids should stop their use before surgery, as continued steroid use after gland excision can stimulate residual tissue and cause recurrence. Patients with medical causes of gynecomastia should have those conditions assessed and managed before or alongside the surgical treatment.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const PatientResultsSection = () => <PatientTestimonials procedureName="Gynecomastia" />;

const FAQSection = () => (
  <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
      <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
      <div className="max-w-3xl mx-auto">
        <Accordion type="single" collapsible className="w-full">
          {GYNECO_FAQS.map((faq, index) => (
            <AccordionItem key={faq.question} value={`gyneco-${index}`} className="border-b border-deep-navy/10">
              <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                <span className="pr-4">{faq.question}</span>
                <div className="flex-shrink-0">
                  <Plus className="w-5 h-5 text-gold" />
                  <Minus className="w-5 h-5 text-gold hidden" />
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </section>
);

const ConsultationCTASection = () => (
  <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
      <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Gynecomastia Consultation</h2>
      <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
        During your consultation, Dr. Jain assesses the grade and composition of your gynecomastia, determines whether the condition is from glandular tissue, fat, or both, and designs a surgical plan combining liposuction and gland excision in the proportion appropriate for your specific presentation. You leave with a clear understanding of what the surgery involves, what the result will look like, and what recovery requires.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
        <Button
          asChild
          className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
          data-testid="cta-book-consultation"
        >
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
            Book Consultation
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </Button>
        <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
      </div>
    </div>
  </section>
);

const RelatedProceduresSection = () => {
  const procedures = [
    {
      name: "Male Nipple Reduction",
      description: "Refine nipple size and projection alongside chest correction for complete chest aesthetics.",
      href: MALE_NIPPLE_PATH,
    },
    {
      name: "Liposuction",
      description: "Body contouring for men addressing chest fat alongside other areas in a single procedure.",
      href: LIPO_PATH,
    },
    {
      name: "Buccal Fat Removal",
      description: "Facial contouring for men seeking definition alongside chest aesthetics.",
      href: BUCCAL_PATH,
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const GynecomastiaPage = () => (
  <>
    <SEO
      title="Gynecomastia Surgery | Male Chest Reduction by Dr. Vicky Jain"
      description="Enlarged male breast tissue causing self-consciousness and clothing avoidance. Dr. Vicky Jain performs gynecomastia surgery permanently. Book your consultation."
      canonical={PAGE_PATH}
    />
    <GynecomastiaJsonLd />
    <Breadcrumb />
    <HeroSection />
    <WhatIsSection />
    <DrJainApproachSection />
    <TypesGradesSection />
    <CausesSection />
    <ExerciseMisconceptionSection />
    <SurgicalTechniqueSection />
    <RecoveryTimelineSection />
    <GoodCandidateSection />
    <PatientResultsSection />
    <FAQSection />
    <ConsultationCTASection />
    <RelatedProceduresSection />
  </>
);

export default GynecomastiaPage;
